import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, LifeBuoy, Paperclip, X, FileText, Image } from "@/components/icons";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/toast-error";

interface SupportTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ProgramSummary {
  id: string;
  title: string;
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: "account", label: "Account" },
  { value: "billing", label: "Billing" },
  { value: "program_content", label: "Program content" },
  { value: "technical", label: "Technical issue" },
  { value: "other", label: "Other" },
];

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 5;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith("image/")) return <Image className="w-3.5 h-3.5 shrink-0 text-brand-500" />;
  return <FileText className="w-3.5 h-3.5 shrink-0 text-red-500" />;
}

export default function SupportTicketDialog({
  open,
  onOpenChange,
}: SupportTicketDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [programId, setProgramId] = useState<string>("none");
  const [links, setLinks] = useState<string[]>([]);
  const [linkDraft, setLinkDraft] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);

  const { data: programs } = useQuery<ProgramSummary[]>({
    queryKey: ["/api/enrollments/mine/programs"],
    enabled: open,
  });

  const reset = () => {
    setSubject("");
    setCategory("other");
    setDescription("");
    setProgramId("none");
    setLinks([]);
    setLinkDraft("");
    setAttachments([]);
  };

  const createTicket = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/support/tickets", {
        subject: subject.trim(),
        category,
        description: description.trim(),
        programId: programId === "none" ? null : programId,
        links: links.filter(Boolean),
      });
      const data = await res.json();

      if (attachments.length > 0 && data?.id && data?.initialMessageId) {
        const formData = new FormData();
        formData.append("messageId", data.initialMessageId);
        for (const file of attachments) {
          formData.append("files", file);
        }
        await fetch(`/api/support/tickets/${data.id}/attachments`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
      }

      return data;
    },
    onSuccess: (ticket) => {
      toast({
        title: "Ticket submitted",
        description: "We'll be in touch shortly.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/unread-count"] });
      reset();
      onOpenChange(false);
      if (ticket?.id) navigate(`/support/${ticket.id}`);
    },
    onError: (err: Error) => {
      toastApiError(err, toast, "Could not submit ticket");
    },
  });

  const addLink = () => {
    const trimmed = linkDraft.trim();
    if (!trimmed) return;
    try {
      new URL(trimmed);
      setLinks([...links, trimmed]);
      setLinkDraft("");
    } catch {
      toast({
        title: "Invalid URL",
        description: "Links must be a full URL like https://…",
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const combined = [...attachments, ...selected];

    const invalid = selected.filter((f) => !ALLOWED_TYPES.includes(f.type));
    if (invalid.length > 0) {
      toast({
        title: "Unsupported file type",
        description: "Only PNG, JPEG, WebP, GIF, or PDF files are allowed.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const tooLarge = selected.filter((f) => f.size > MAX_FILE_SIZE);
    if (tooLarge.length > 0) {
      toast({
        title: "File too large",
        description: "Each file must be 10MB or smaller.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    if (combined.length > MAX_FILES) {
      toast({
        title: "Too many files",
        description: `You can attach up to ${MAX_FILES} files per message.`,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    setAttachments(combined);
    e.target.value = "";
  };

  const removeAttachment = (idx: number) => {
    setAttachments(attachments.filter((_, i) => i !== idx));
  };

  const canSubmit = subject.trim().length >= 3 && description.trim().length >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Inline style forces display:flex over the base `grid` from shadcn's DialogContent.
        Tailwind class-based `flex` can lose the cascade race against `grid` in the
        generated CSS, so we use style to guarantee the flex layout.
        padding:0 and gap:0 strip the base p-6/gap-4 so child divs control their own spacing.
      */}
      <DialogContent
        className="max-w-xl overflow-hidden"
        style={{ display: "flex", flexDirection: "column", maxHeight: "90vh", padding: 0, gap: 0 }}
        data-testid="dialog-support-ticket"
      >
        {/* Header — pr-12 leaves room for the absolute-positioned X close button */}
        <div className="px-6 pt-6 pb-4 pr-12 shrink-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-[var(--navy)]" />
              Submit a support ticket
            </DialogTitle>
            <DialogDescription>
              Tell us what's going on. A support team member will get back to you.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable body — flex-1 + min-h-0 lets this shrink and scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-subject">Subject</Label>
            <Input
              id="ticket-subject"
              data-testid="input-ticket-subject"
              placeholder="Short summary"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
            {subject.length > 0 && subject.trim().length < 3 && (
              <p className="text-xs text-amber-600" data-testid="text-subject-hint">
                At least 3 characters required
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ticket-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="ticket-category" data-testid="select-ticket-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-program">Related program (optional)</Label>
              <Select value={programId} onValueChange={setProgramId}>
                <SelectTrigger id="ticket-program" data-testid="select-ticket-program">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(programs || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ticket-description">Description</Label>
            <Textarea
              id="ticket-description"
              data-testid="input-ticket-description"
              placeholder="Describe what happened, what you expected, and any steps to reproduce."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              maxLength={10000}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Links (optional)</Label>
            <div className="flex gap-2">
              <Input
                data-testid="input-ticket-link"
                placeholder="https://..."
                value={linkDraft}
                onChange={(e) => setLinkDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addLink();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={addLink}
                disabled={!linkDraft.trim()}
                data-testid="button-ticket-add-link"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {links.length > 0 && (
              <ul className="space-y-1 mt-1.5">
                {links.map((l, i) => (
                  <li
                    key={`${l}-${i}`}
                    className="flex items-center justify-between gap-2 text-xs bg-gray-50 border rounded px-2 py-1"
                    data-testid={`text-ticket-link-${i}`}
                  >
                    <span className="truncate text-brand-700">{l}</span>
                    <button
                      type="button"
                      onClick={() => setLinks(links.filter((_, idx) => idx !== i))}
                      className="text-gray-500 hover:text-red-600"
                      aria-label="Remove link"
                      data-testid={`button-ticket-remove-link-${i}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Attachments (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Images (PNG, JPEG, WebP, GIF) or PDFs · max 10MB each · up to {MAX_FILES} files
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,image/png,image/jpeg,image/webp,image/gif,application/pdf"
              multiple
              className="hidden"
              onChange={handleFileChange}
              data-testid="input-ticket-files"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.length >= MAX_FILES}
              data-testid="button-ticket-attach-files"
            >
              <Paperclip className="w-3.5 h-3.5 mr-1.5" />
              Attach files
            </Button>
            {attachments.length > 0 && (
              <ul className="space-y-1 mt-1.5">
                {attachments.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2 text-xs bg-gray-50 border rounded px-2 py-1"
                    data-testid={`text-ticket-attachment-${i}`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <FileIcon mimeType={f.type} />
                      <span className="truncate">{f.name}</span>
                      <span className="text-muted-foreground shrink-0">({formatBytes(f.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="text-gray-500 hover:text-red-600 shrink-0"
                      aria-label={`Remove ${f.name}`}
                      data-testid={`button-ticket-remove-attachment-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Pinned footer — always visible, never scrolls away */}
        <div className="px-6 py-4 border-t shrink-0">
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              data-testid="button-ticket-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => createTicket.mutate()}
              disabled={!canSubmit || createTicket.isPending}
              className="bg-[var(--navy)] hover:bg-[var(--navy)]/90"
              data-testid="button-ticket-submit"
            >
              {createTicket.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Submit ticket
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
